<template>
  <Section
    :id="id"
    :header="t('documents.documents', 2)"
    :loading="documents.loading"
    class="documents-section"
    data-class="documents-container flex flex-gap-1 flex-column"
  >
    <template #headerRight>
      <RygenButton
        mode="secondary"
        icon="pi pi-file"
        :label="t('documents.manageDocuments')"
        :disabled="documents.loading"
        @click="showManageDocumentsModal = true"
      />
    </template>

    <SimpleDocumentCard
      v-for="document in documents.data"
      :key="document.fileName"
      :document="document"
      :name="document.documentName ?? document.fileName"
      previewable
    />

    <p v-if="!documents.loading && !documents.data?.length">
      {{
        t('common.noParam', {
          param: t('documents.documents', 2),
        })
      }}
    </p>
  </Section>

  <ManageDocumentsModal
    v-if="showManageDocumentsModal"
    v-model:visible="showManageDocumentsModal"
    :editable="editable"
  />
</template>

<script lang="ts" setup>
import { defineAsyncComponent, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useDocuments } from '@/composables';
import { useParentEntityStore } from '@/stores/ParentEntity';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';

const SimpleDocumentCard = defineAsyncComponent(
  // lazy load document components so that the huge pdfjs files are lazy loaded
  () => import('@rygen/rygen-ui/components/document/SimpleDocumentCard.vue'),
);
const ManageDocumentsModal = defineAsyncComponent(
  // lazy load document components so that the huge pdfjs files are lazy loaded
  () => import('@/components/document/ManageDocumentsModal.vue'),
);

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
});
const { id, editable } = toRefs(props);

const { documents, loadDocuments } = useDocuments();
const { parentEntity } = useParentEntityStore();
const { t } = useI18n();

const showManageDocumentsModal = ref(false);

watch(
  () => parentEntity.value?.id,
  (newVal: number | null | undefined) => {
    if (newVal) {
      loadDocuments();
    }
  },
  { deep: true, immediate: true },
);
</script>
